import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../supabaseClient';
import {
  getChatMessages,
  markChatMessageRead,
  markChatMessagesRead,
  sendChatMessage,
} from '../../../services/api/trips-ui';
import { useAuth } from '../../USER/Auth/AuthContext';
import PaymentPanel from '../Payment/PaymentPanel';
import { notifyNewChatMessage } from '../../../services/pushNotifications';
import { SERVICE_STATUSES } from '../../../constants';
import { Send, X, CheckCheck, Banknote, MessageSquare, Loader2 } from 'lucide-react';

const TYPING_TIMEOUT = 2000;

function LiveChat({ show, onHide, serviceId, service }) {
  const { currentUser, isDriver } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [typingUser, setTypingUser] = useState(null);
  const [closing, setClosing] = useState(false);
  const [sendError, setSendError] = useState(null);
  const messagesEndRef = useRef(null);
  const typingRef = useRef(null);
  const presenceChannelRef = useRef(null);
  const [showPayment, setShowPayment] = useState(false);
  const [isClosingAnim, setIsClosingAnim] = useState(false);

  const otherUid = service?.driver_uid === currentUser?.id ? service?.user_uid : service?.driver_uid;

  const isPaymentMsg = (msg) => msg.message_type === 'payment_system';

  // Handle Close
  const handleHide = () => {
    onHide();
  };

  // Auto-close cuando el servicio se completa o cancela
  useEffect(() => {
    if (!show || !serviceId) return;

    const ch = supabase
      .channel(`svc_status_${serviceId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'services',
        filter: `service_id=eq.${serviceId}`,
      }, (payload) => {
        const st = payload.new.status;
        if (st === SERVICE_STATUSES.COMPLETED || st === SERVICE_STATUSES.CANCELLED) {
          setClosing(true);
          setTimeout(() => { handleHide(); setClosing(false); }, 1500);
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [show, serviceId]);

  // Typing indicator via Presence
  useEffect(() => {
    if (!show || !serviceId || !currentUser) return;

    const presenceChannel = supabase.channel(`chat_typing_${serviceId}`, {
      config: { presence: { key: currentUser.id } },
    });

    presenceChannelRef.current = presenceChannel;

    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        const typing = Object.values(state)
          .flat()
          .find(p => p.user_id !== currentUser.id && p.typing);
        setTypingUser(typing ? typing.user_name : null);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await presenceChannel.track({ user_id: currentUser.id, user_name: currentUser.name || '', typing: false });
        }
      });

    return () => { 
      supabase.removeChannel(presenceChannel); 
      presenceChannelRef.current = null;
    };
  }, [show, serviceId, currentUser]);

  const broadcastTyping = useCallback(async (isTyping) => {
    if (!serviceId || !presenceChannelRef.current || !currentUser) return;
    try {
      await presenceChannelRef.current.track({ user_id: currentUser.id, user_name: currentUser.name || '', typing: isTyping });
    } catch (e) {
      console.error("Error broadcasting typing", e);
    }
  }, [serviceId, currentUser]);

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    if (!typingRef.current) {
      typingRef.current = setTimeout(() => { typingRef.current = null; }, TYPING_TIMEOUT);
      broadcastTyping(true);
    }
  };

  const handleBlur = () => {
    if (typingRef.current) {
      clearTimeout(typingRef.current);
      typingRef.current = null;
    }
    broadcastTyping(false);
  };

  // Chat messages subscription
  useEffect(() => {
    if (!show || !serviceId || !currentUser) return;

    const fetchMessages = async () => {
      setLoading(true);
      const { data, error } = await getChatMessages(serviceId);

      if (!error && data) {
        setMessages(data);
        const unreadIds = data
          .filter(m => !m.is_read && m.sender_uid !== currentUser.id)
          .map(m => m.id);
        if (unreadIds.length > 0) {
          markChatMessagesRead(unreadIds).then();
        }
      }
      setLoading(false);
    };

    fetchMessages();

    const channel = supabase
      .channel(`chat_service_${serviceId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `service_id=eq.${serviceId}`
      }, payload => {
        setMessages(prev => [...prev, payload.new]);
        if (payload.new.sender_uid !== currentUser.id) {
          markChatMessageRead(payload.new.id).then();
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'chat_messages',
        filter: `service_id=eq.${serviceId}`
      }, payload => {
        setMessages(prev => prev.map(m => m.id === payload.new.id ? payload.new : m));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [show, serviceId, currentUser]);

  useEffect(() => {
    if (show) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, show, typingUser]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser) return;
    setSendError(null);

    const text = newMessage.trim();
    const tempId = `temp_${Date.now()}`;
    const optimisticMsg = {
      id: tempId,
      service_id: serviceId,
      sender_uid: currentUser.id,
      message: text,
      message_type: 'text',
      is_read: false,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage('');
    handleBlur();

    const { error } = await sendChatMessage(serviceId, currentUser.id, text);

    if (error) {
      console.error("Error sending message", error);
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(text);
      setSendError('No se pudo enviar el mensaje. Intenta de nuevo.');
    } else if (otherUid) {
      notifyNewChatMessage(serviceId, otherUid, currentUser?.name || currentUser?.email, text);
    }
  };

  const theme = {
    bg: isDriver ? 'bg-gray-900' : 'bg-gray-50',
    headerBg: isDriver ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100',
    textPrimary: isDriver ? 'text-white' : 'text-slate-800',
    textSecondary: isDriver ? 'text-gray-400' : 'text-gray-500',
    iconBg: isDriver ? 'bg-cyan-900/30 text-cyan-400' : 'bg-cyan-50 text-cyan-600',
    btnLightBg: isDriver ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
    btnActiveBg: 'bg-[var(--bry-accent)] text-white',
    myMsgBg: isDriver ? 'bg-[var(--bry-accent)] text-white' : 'bg-[var(--bry-accent)] text-white',
    theirMsgBg: isDriver ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800 shadow-sm border border-gray-100',
    inputBg: isDriver ? 'bg-gray-800 text-white placeholder-gray-500' : 'bg-gray-100 text-slate-800 placeholder-gray-400',
    inputContainer: isDriver ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100',
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40" 
            onClick={handleHide}
          />
          
          <motion.div 
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={`fixed bottom-0 left-0 right-0 z-50 h-[85vh] rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.2)] flex flex-col ${theme.bg}`}
          >
            {/* Header */}
        <div className={`flex items-center gap-3 p-4 border-b rounded-t-3xl ${theme.headerBg}`}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${theme.iconBg}`}>
            <MessageSquare size={20} />
          </div>
          <div className="flex-1">
            <h3 className={`font-bold text-base m-0 ${theme.textPrimary}`}>Chat del Servicio</h3>
            <p className={`text-xs m-0 ${theme.textSecondary}`}>Comunicación en tiempo real</p>
          </div>
          <button
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${showPayment ? theme.btnActiveBg : theme.btnLightBg}`}
            onClick={() => setShowPayment(!showPayment)}
            title="Mostrar Panel de Pagos"
          >
            <Banknote size={18} />
          </button>
          <button
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${theme.btnLightBg}`}
            onClick={handleHide}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col flex-1 relative overflow-hidden">
          {closing && (
            <div className="text-center py-2 bg-yellow-400 text-yellow-900 text-sm font-semibold sticky top-0 z-10 flex items-center justify-center shadow-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Servicio finalizado — cerrando chat...
            </div>
          )}
          
          {showPayment && (
            <div className={`shadow-sm border-b z-10 ${theme.headerBg}`}>
              <PaymentPanel service={service} onRefresh={() => {}} />
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 scroll-smooth">
            {loading ? (
              <div className={`flex flex-col items-center justify-center h-full ${theme.textSecondary}`}>
                <Loader2 size={32} className="animate-spin text-[var(--bry-accent)] mb-3" />
                <span className="text-sm">Cargando mensajes...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className={`flex flex-col items-center justify-center h-full opacity-60 ${theme.textSecondary}`}>
                <MessageSquare size={48} className="mb-4" />
                <p className="font-semibold mb-1">No hay mensajes aún.</p>
                <span className="text-xs">Escribe abajo para empezar la conversación.</span>
              </div>
            ) : (
              <div className="flex flex-col gap-4 pb-2">
                {messages.map((msg) => {
                  if (isPaymentMsg(msg)) {
                    const meta = msg.metadata || {};
                    return (
                      <div key={msg.id} className="flex justify-center my-2">
                        <div className="px-4 py-2 rounded-full shadow-sm flex items-center gap-2 bg-amber-100 text-amber-800 text-xs border border-amber-200">
                          <Banknote size={14} />
                          <span>{msg.message}</span>
                          {meta.amount && (
                            <strong className="ml-1">{meta.amount} {meta.currency}</strong>
                          )}
                        </div>
                      </div>
                    );
                  }
                  const isMine = currentUser && msg.sender_uid === currentUser.id;
                  return (
                    <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                      <div
                        className={`px-4 py-2.5 max-w-[85%] text-sm leading-relaxed ${isMine ? theme.myMsgBg : theme.theirMsgBg}`}
                        style={{
                          borderTopLeftRadius: '18px',
                          borderTopRightRadius: '18px',
                          borderBottomLeftRadius: isMine ? '18px' : '4px',
                          borderBottomRightRadius: isMine ? '4px' : '18px',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.message}
                      </div>
                      <div className={`flex items-center mt-1 gap-1 px-1 text-[11px] ${theme.textSecondary}`}>
                        <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {isMine && (
                          <CheckCheck
                            size={14}
                            className={`ml-1 ${msg.is_read ? 'text-[#34B7F1]' : theme.textSecondary}`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {typingUser && (
              <div className="flex items-center gap-2 mt-2 mb-2 px-2">
                <div className={`flex items-center justify-center px-3 py-2.5 rounded-2xl rounded-bl-sm shadow-sm ${isDriver ? 'bg-gray-800' : 'bg-white border border-gray-100'}`}>
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--bry-accent)] animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--bry-accent)] animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--bry-accent)] animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
                <span className={`text-[11px] italic ${theme.textSecondary}`}>
                  {typingUser} está escribiendo...
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {sendError && (
            <div className="text-center py-2 text-red-500 bg-red-50 text-xs border-t border-red-100 flex items-center justify-center">
              <X size={14} className="mr-1" /> {sendError}
            </div>
          )}
          
          <div className={`p-2 pb-safe-or-3 border-t shadow-[0_-4px_15px_rgba(0,0,0,0.03)] z-10 ${theme.inputContainer}`}>
            {/* Plantillas de mensajes nativas de casos de uso */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-1 scrollbar-none no-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
              {(isDriver ? [
                '📍 Ya llegué al punto de encuentro.',
                '🏍️ Estoy en camino a tu ubicación.',
                '⏱️ Hay tráfico pesado (aprox. 5 min).',
                '📦 Paquete asegurado en la moto.',
                '🏁 Llegué al destino de entrega.',
                '💬 ¿Algún punto de referencia adicional?'
              ] : [
                '📍 Estoy afuera esperando en el punto.',
                '⏳ Estoy bajando en 1 minuto.',
                '🚪 Casa/edificio de portón visible.',
                '📦 Paquete frágil, favor cuidar.',
                '💵 Pagaré en efectivo / Pago Móvil.',
                '👍 ¡Entendido, muchas gracias!'
              ]).map((template, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setNewMessage(template);
                  }}
                  className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap transition-all shrink-0 ${
                    isDriver 
                      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700' 
                      : 'bg-cyan-50 text-cyan-900 hover:bg-cyan-100 border border-cyan-200'
                  }`}
                >
                  {template}
                </button>
              ))}
            </div>

            <form onSubmit={sendMessage} className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Escribe un mensaje o elige una plantilla..."
                  value={newMessage}
                  onChange={handleTyping}
                  onBlur={handleBlur}
                  disabled={closing}
                  className={`w-full rounded-full py-2.5 px-4 text-sm outline-none transition-colors ${theme.inputBg} focus:ring-2 focus:ring-[var(--bry-accent)]/30`}
                />
              </div>
              <button
                type="submit"
                disabled={!newMessage.trim() || closing}
                className={`flex items-center justify-center w-11 h-11 rounded-full shadow-sm transition-all duration-200 shrink-0 ${
                  newMessage.trim() 
                    ? 'bg-[var(--bry-accent)] text-white hover:bg-[#0c959a] hover:shadow-[0_0_15px_rgba(14,171,176,0.4)]' 
                    : isDriver ? 'bg-gray-800 text-gray-600' : 'bg-gray-200 text-gray-400'
                }`}
              >
                <Send size={18} className="ml-[-2px]" />
              </button>
            </form>
          </div>
        </div>
      </motion.div>
    </>
      )}
    </AnimatePresence>
  );
}

export default LiveChat;

